import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import {
  ADD_BANK_ACCOUNT_HOLDER_NAME,
  ADD_BANK_ACCOUNT_NUMBER_NAME,
  ADD_BANK_NAME,
  DESKTOP_MIN_WIDTH,
} from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';

const BANKS_SEARCH_NAME = 'banks-search-field';

const CustomBankNameDropdown = ({
  config,
  banks,
  bankExisteds,
  information,
  setInformation,
  validation,
  setValidation,
  currentBankSelected,
  setCurrentBankSelected,
  checkBankAccountExisted,
  setSaveDisableInit,
  accountHolderName,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [banksSearch, setBanksSearch] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);
  const [isChecking, setChecking] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);

  const isApiEnabled = config?.apiEnabled;

  const numberMaxLength = config?.max;
  const numberMinLength = config?.min;

  useInputFocus(BANKS_SEARCH_NAME);

  useEffect(() => {
    const accountNumber = information[ADD_BANK_ACCOUNT_NUMBER_NAME];
    const isCheckNumber =
      accountNumber?.length <= numberMaxLength &&
      accountNumber?.length >= numberMinLength &&
      !validation[ADD_BANK_ACCOUNT_NUMBER_NAME];

    if (isCheckNumber && isChecking) {
      handleNumberValid(
        accountNumber,
        ADD_BANK_ACCOUNT_NUMBER_NAME,
        validation,
        information,
        currentBankSelected?.id
      );
    }
  }, [isChecking, currentBankSelected]);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setBanksSearch(null);

      return;
    }

    const newBankSearch = banks?.filter(
      (bank) =>
        bank?.name?.toLowerCase()?.includes(value?.toLowerCase()) && bank
    );
    setBanksSearch(newBankSearch);
  };

  const checkNumberValid = async (
    value,
    name,
    validation,

    information,

    bankId
  ) => {
    setValidation(validation);
  };
  const { debounce: handleNumberValid } = useDebounceCallback(
    (value, name, validation, information, bankId) => {
      checkNumberValid(value, name, validation, information, bankId);
    },
    0
  );

  const { debounce: handleAllowCheckAccountNumber } = useDebounceCallback(
    (validation) => {
      if (validation[ADD_BANK_ACCOUNT_NUMBER_NAME]) {
        setChecking(false);
      } else {
        setChecking(true);
      }
    },
    0
  );

  const handleBankSelected = async (bank) => {
    setCurrentBankSelected(bank);

    setSearchValue('');

    setBanksSearch(null);

    setShowDropdown(false);

    if (isApiEnabled) {
      setChecking(false);
    }

    const newInformation = {
      ...information,
      [ADD_BANK_NAME]: bank.name,
      ...(accountHolderName && {
        [ADD_BANK_ACCOUNT_HOLDER_NAME]: accountHolderName,
      }),
    };
    setInformation(newInformation);

    let newValidation = {
      ...validation,
      [ADD_BANK_ACCOUNT_NUMBER_NAME]: '',
    };

    newValidation = checkBankAccountExisted(
      bankExisteds,
      newInformation,
      newValidation
    );

    if (newValidation[ADD_BANK_ACCOUNT_NUMBER_NAME].includes('exist')) {
      setValidation(newValidation);

      return;
    }

    if (
      newInformation[ADD_BANK_ACCOUNT_NUMBER_NAME] &&
      !newValidation[ADD_BANK_ACCOUNT_NUMBER_NAME]
    ) {
      handleAllowCheckAccountNumber(newValidation);
    }

    setInformation(newInformation);

    setValidation(newValidation);

    setSaveDisableInit && setSaveDisableInit(false);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setBanksSearch(null);
    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        name={BANKS_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(isSearchInvalid ? [] : banksSearch || banks)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={() => handleBankSelected(item)}
          >
            {item.name}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <BankDropdownStyled>
      <Label>
        {t('label_bank_name')}
        <span>*</span>
      </Label>
      <DropdownCommon
        value={currentBankSelected?.name}
        placeholder={'form_placeholder_please_select'}
        keyError={validation?.[ADD_BANK_NAME]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </BankDropdownStyled>
  );
};

const BankDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  margin-bottom: 8px;

  & span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomBankNameDropdown;
